/* eslint-disable @typescript-eslint/no-explicit-any */
import { Input } from '@/components/ui/input';
import { useEffect, useState } from 'react';
import { ArrowLeft, CircleX, ExternalLink, Tag, Tags } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCollections } from '@/hooks/api/dashboard/colletions';
import { StoreCollectionListResponse, StoreProductListResponse } from '@/types/http';
import { useProducts } from '@/hooks/api/dashboard/products';
import Image from 'next/image';
import LoadingAnimationIcon from '@/components/icons/loading-animation-icon';
import { useCustomPages } from '@/hooks/api/dashboard/custom-pages';
import { useBlogs } from '@/hooks/api/dashboard/blogs';
import { useBlogPosts } from '@/hooks/api/dashboard/blog-posts';
import { truncateText } from '@/lib/utils';
import { MENU_NAVIGATION_ITEMS } from '@/constant/nav-link';

type MenuItemType = {
  label: string;
  value: string;
  icon: any;
  image: string;
};

type InputLinkTypes = {
  name: string;
  placeholder?: string;
  onChange: (value: string) => void;
  value: string;
};

export default function InputLink({
  name,
  placeholder = 'Select or paste link',
  onChange,
  value,
}: InputLinkTypes) {
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [selectedMenu, setSelectedMenu] = useState(null);
  const [listItems, setListItems] = useState<MenuItemType[]>(MENU_NAVIGATION_ITEMS as any);
  const [inputValue, setInputValue] = useState('');
  const [manualInput, setManualInput] = useState('');
  const [page, _setPage] = useState<number>(1);

  const fetchQueryParams = {
    page,
    pageSize: 10,
    q: manualInput,
  };

  const { data: collections, isFetching: isLoadingCollections } = useCollections(fetchQueryParams, {
    select: (data: StoreCollectionListResponse) =>
      ({
        ...data,
        data: data.data.map((item) => ({
          label: item.collection_name,
          value: item.slug,
          icon: '',
          image: item.img_url,
        })),
      } as any),
  });

  const { data: products, isFetching: isLoadingProducts } = useProducts(fetchQueryParams, {
    select: (data: StoreProductListResponse) => {
      const formattedSlug = (link: string) => {
        if (link) null;
        const url = link.split('/');
        return url.pop() || url.pop();
      };

      return {
        ...data,
        data: data.data.map((item: any) => ({
          label: item.product_name,
          value: formattedSlug(item.seo_link),
          icon: '',
          image: item.product_assets?.[0]?.assets_url,
        })) as any,
      };
    },
  });

  const { data: customPages, isFetching: isLoadingCustomPages } = useCustomPages(fetchQueryParams, {
    select: (data) =>
      ({
        ...data,
        data: data.data.map((item) => ({
          label: item.title,
          value: item.slug,
          icon: '',
          image: '',
        })),
      } as any),
  });

  const { data: blogs, isFetching: isLoadingBlogs } = useBlogs(fetchQueryParams, {
    select: (data) =>
      ({
        ...data,
        data: data.data.map((item) => ({
          label: item.name,
          value: item.slug,
          icon: '',
          image: '',
        })),
      } as any),
  });

  const { data: blogPosts, isFetching: isLoadingBlogPosts } = useBlogPosts(fetchQueryParams, {
    select: (data) =>
      ({
        ...data,
        data: data.data.map((item) => ({
          label: item.title,
          value: `${item.slug_category}/${item.slug}`,
          icon: '',
          image: item.image_banner,
        })),
      } as any),
  });

  useEffect(() => {
    setInputValue(value);
  }, []);

  useEffect(() => {
    onChange(inputValue);
  }, [inputValue]);

  useEffect(() => {
    if (selectedMenu === 'products' && products) {
      setListItems([
        {
          label: 'All Products',
          value: 'all',
          icon: Tag,
          image: '',
        },
        ...(products as any),
      ]);
    } else if (selectedMenu === 'collections' && collections) {
      setListItems([
        {
          label: 'All Collections',
          value: `collection-list`,
          icon: Tags,
          image: '',
        },
        ...(collections as any),
      ]);
    } else if (selectedMenu === 'pages') {
      setListItems(customPages as any);
    } else if (selectedMenu === 'blogs' && blogs) {
      setListItems(blogs as any);
    } else if (selectedMenu === 'blog-posts' && blogPosts) {
      setListItems(blogPosts as any);
    } else if (inputValue && inputValue.includes('.')) {
      setListItems([
        {
          label: inputValue,
          value: `https://${inputValue}`,
          icon: ExternalLink,
          image: '',
        },
      ]);
    }
  }, [manualInput, products, collections, selectedMenu]);

  const handleMenuClick = (menuItem: any) => {
    setSelectedMenu(menuItem.value);
    setInputValue('');
    setManualInput('');

    if (menuItem.value === 'products' && products) {
      setListItems(products as any);
    } else if (menuItem.value === 'collections' && collections) {
      setListItems(collections as any);
    } else if (menuItem.value === 'pages') {
      setListItems(customPages as any);
    } else if (menuItem.value === 'blogs') {
      setListItems(blogs as any);
    } else if (menuItem.value === 'blog-posts') {
      setListItems(blogPosts as any);
    }
  };

  const handleItemClick = (item: any) => {
    if (selectedMenu) {
      if (item.value === 'collection-list') {
        setInputValue(item.value);
      } else if (selectedMenu === 'blog-posts') {
        setInputValue(`blogs/${item.value}`);
      } else if (item.value === 'all') {
        setInputValue(`collections/all`);
      } else {
        setInputValue(`${selectedMenu}/${item.value}`);
      }
      onChange(inputValue);
    } else {
      setInputValue(item.value);
      onChange(inputValue);
    }
    setIsFocused(false);
    setSelectedMenu(null);
    setListItems(MENU_NAVIGATION_ITEMS as any);
  };

  const handleResetInputUrl = () => {
    setSelectedMenu(null);
    setListItems([]);
    setIsFocused(false);
    setListItems(MENU_NAVIGATION_ITEMS as any);
  };

  const handleBackClick = () => {
    setSelectedMenu(null);
    setListItems(MENU_NAVIGATION_ITEMS as any);
  };

  const handleManualInputChange = (e: any) => {
    setInputValue(e.target.value);
    setManualInput(e.target.value);
  };

  return (
    <div className='relative w-full max-w-full'>
      <div className='flex items-center gap-2'>
        <Input
          type='text'
          name={name}
          className='rounded-md border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500'
          classNameWrapper='w-full'
          onFocus={() => setIsFocused(true)}
          // onBlur={() => setTimeout(() => setIsFocused(false), 100)}
          value={inputValue}
          onChange={handleManualInputChange}
          placeholder={placeholder}
          autoComplete='off'
        />

        <Button type='button' variant='secondary' onClick={handleResetInputUrl}>
          <CircleX className='h-4 w-4 text-gray-800' />
        </Button>
      </div>

      {/* ListBox */}
      {isFocused && (
        <>
          <ul className='absolute z-10 mt-1 max-h-60 w-[90%] overflow-auto rounded-md border border-gray-300 bg-white shadow-lg'>
            {(selectedMenu === 'products' ||
              selectedMenu === 'collections' ||
              selectedMenu === 'pages' ||
              selectedMenu === 'blogs' ||
              selectedMenu === 'blog-posts') && (
              <li
                className='flex cursor-pointer items-center gap-2 px-4 py-2 text-sm hover:bg-blue-100'
                onClick={handleBackClick}
              >
                <ArrowLeft className='h-4 w-4' />
                Back
              </li>
            )}
            {isLoadingProducts ||
            isLoadingCollections ||
            isLoadingCustomPages ||
            isLoadingBlogs ||
            isLoadingBlogPosts ? (
              <div className='p-4'>
                <LoadingAnimationIcon />
              </div>
            ) : (
              listItems.map((item, index) => (
                <li
                  key={index}
                  onClick={() => {
                    if (
                      item.value === 'products' ||
                      item.value === 'collections' ||
                      item.value === 'pages' ||
                      item.value === 'blogs' ||
                      item.value === 'blog-posts'
                    ) {
                      handleMenuClick(item);
                    } else {
                      handleItemClick(item);
                    }
                  }}
                  className='flex cursor-pointer items-center gap-2 px-4 py-2 text-sm hover:bg-blue-100'
                >
                  {item.icon && <item.icon className='h-4 w-4' />}
                  {item.image && <Image src={item.image} width={20} height={20} alt={item.label} />}
                  {truncateText(item.label, 15)}
                </li>
              ))
            )}
            {listItems.length === 0 && <li className='px-4 py-2 text-gray-500'>No items found</li>}
          </ul>
        </>
      )}
    </div>
  );
}
